import * as React from "react"
import { createPortal } from "react-dom"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "@/lib/utils"
import { X } from "lucide-react"

interface BottomSheetProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  className?: string
}

export function BottomSheet({
  isOpen,
  onClose,
  children,
  className
}: BottomSheetProps) {
  // Portal container ref
  const [isMounted, setIsMounted] = React.useState(false)
  
  // Mount the component on client side only to avoid SSR issues
  React.useEffect(() => {
    setIsMounted(true)
    return () => setIsMounted(false)
  }, [])

  // Close on escape key
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      // Prevent background scrolling
      document.body.style.overflow = 'hidden'
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape)
      // Restore scrolling when sheet is closed
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  const content = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <React.Fragment key="bottom-sheet-content">
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 z-50"
          />
          
          {/* Sheet */}
          <motion.div
            key="sheet"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className={cn(
              "fixed bottom-0 left-0 right-0 z-50",
              "max-h-[85vh] overflow-y-auto",
              "bg-white dark:bg-gray-900 rounded-t-[20px]",
              className
            )}
          >
            {/* Handle and close button */}
            <div className="sticky top-0 flex items-center justify-between px-6 py-3 bg-white dark:bg-gray-900 rounded-t-[20px] z-10">
              <button 
                type="button"
                onClick={onClose}
                className="absolute left-4 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Close"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
              <div className="w-10 h-1 bg-gray-200 dark:bg-gray-700 rounded-full mx-auto" />
              <div className="w-5" /> {/* Spacer for balance */}
            </div>
            
            {children}
          </motion.div>
        </React.Fragment>
      )}
    </AnimatePresence>
  )

  if (!isMounted) return null

  // Use portal to render the sheet at the document body level
  return createPortal(content, document.body)
} 